'use client'

import {
    HrEmployee,
    getHrEmployees,
    getHrEmployee,
} from '@/lib/api/hr/hr-employee-api'
import { useEffect, useRef, useState } from 'react'
import { MdBadge, MdClose, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (employeeId: number | null, employee: HrEmployee | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
}

export function EmployeeSelector({
    value,
    onChange,
    label = 'Funcionário',
    placeholder = 'Buscar funcionário...',
    disabled = false,
    className = '',
}: Props) {
    const [results, setResults] = useState<HrEmployee[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selected, setSelected] = useState<HrEmployee | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Hidrata seleção quando value muda externamente
    useEffect(() => {
        if (!value) {
            setSelected(null)
            return
        }
        if (selected?.id === value) return
        getHrEmployee(value)
            .then(res => {
                if (res.success && res.result) setSelected(res.result)
            })
            .catch(() => {})
    }, [value, selected?.id])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        if (showDropdown) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showDropdown])

    const doSearch = (term: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            setLoading(true)
            try {
                const res = await getHrEmployees({
                    search: term || undefined,
                    limit: 25,
                })
                if (res.success && Array.isArray(res.result))
                    setResults(res.result)
            } catch {
                setResults([])
            } finally {
                setLoading(false)
            }
        }, 300)
    }

    const handleClear = () => {
        setSelected(null)
        onChange(null, null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            <label className="label">
                <span className="label-text">{label}</span>
            </label>

            {selected ? (
                <div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                    <MdBadge className="text-base-content/60" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                            {selected.name}
                        </p>
                        {selected.jobTitle && (
                            <p className="text-xs text-base-content/60 truncate">
                                {selected.jobTitle}
                                {selected.cpf ? ` · CPF ${selected.cpf}` : ''}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={handleClear}
                        disabled={disabled}
                    >
                        <MdClose />
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <div className="relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 z-10" />
                        <input
                            type="text"
                            className="input input-bordered w-full pl-10"
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                                doSearch(e.target.value)
                            }}
                            onFocus={() => {
                                setShowDropdown(true)
                                if (results.length === 0) doSearch('')
                            }}
                            disabled={disabled}
                        />
                    </div>
                    {showDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {loading && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                    Buscando...
                                </div>
                            )}
                            {!loading && results.length === 0 && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    Nenhum funcionário encontrado
                                </div>
                            )}
                            {!loading &&
                                results.map(e => (
                                    <button
                                        key={e.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                        onClick={() => {
                                            setSelected(e)
                                            onChange(e.id, e)
                                            setShowDropdown(false)
                                            setSearchTerm('')
                                        }}
                                    >
                                        <span className="font-medium text-sm">
                                            {e.name}
                                        </span>
                                        {e.jobTitle && (
                                            <span className="text-xs text-base-content/60 ml-2">
                                                {e.jobTitle}
                                            </span>
                                        )}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
